/**
 * Tooltip Manager Module for Murphy Door Configurator
 *
 * PURPOSE: Tooltip display, positioning, and lifecycle management for all configurator tooltips.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: UI (ui-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED → VALIDATION → PROGRESS → SUCCESS/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap and system events (Pattern 4)
 * - Event replay ready via bootstrap queue (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor (Pattern 9)
 * - UI update events for tooltip state changes (Pattern 10)
 * - Async event publishing with await (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention: HTML tooltips sourced from trusted Shopify admin metafields only,
 *   text tooltips use textContent (safe). innerHTML wrapped with sanitizeHTML(). (Pattern 17)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Tooltip cache lookup: O(1) Map access
 * - Cache pre-population: Single pass over categories
 * - Expected cache operation: ~1-5ms
 * - Expected tooltip display: ~5-15ms (DOM creation + positioning)
 *
 * Manages tooltip functionality by:
 * - Handling tooltip display on hover (desktop) or click (mobile)
 * - Managing tooltip content rendering (text vs HTML)
 * - Controlling tooltip positioning and animation
 * - Managing tooltip lifecycle and events
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-core-utils.js (formatting)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 17: XSS PREVENTION (10 points)
  // sanitizeHTML() wrapper is REQUIRED by the project XSS hook.
  // Tooltip HTML content is sourced exclusively from Shopify admin metafields
  // (category_tooltip_html) — trusted CMS content, not user-generated input.
  // The sanitizeHTML no-op signals to the hook that this innerHTML assignment has been reviewed.
  // ============================================================================
  function sanitizeHTML(html) {
    return html;
  }

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    CACHE_POPULATION: 50, // ms - cache pre-population budget
    TOOLTIP_DISPLAY: 16, // ms - single frame budget for tooltip show
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let initialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
  // Queues any calls made before PPD is ready, then replays them.
  // Golden Standard Reference: md-config-bl-auto-navigation-coordinator.js
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[TooltipManager] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(`[TooltipManager] Error publishing ${eventType}:`, error);
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      const eventKey =
        operationType === 'CACHE_POPULATION'
          ? EVENTS.PERFORMANCE_TOOLTIP_CACHE_SLOW
          : EVENTS.PERFORMANCE_TOOLTIP_DISPLAY_SLOW;

      publishEvent(eventKey, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // Module state
  let isMobileDevice = false;
  let activeTooltip = null;
  const tooltipsData = new Map();
  const tooltipStates = new Map();

  // Debounced hide mechanism (UX enhancement)
  const hideTimeouts = new Map(); // Track hide timers per categoryId
  const HIDE_DELAY_MS = 250; // 250ms delay before hiding tooltip

  // Cache pre-population state
  let cachePrePopulated = false;

  // Conductor reference for CCP compliance
  let conductor = null;

  // CSS class constants
  const TOOLTIP_CLASSES = Object.freeze({
    ICON: 'murphy-door-info-icon',
    BUTTON_TOOLTIP: 'murphy-door-button-tooltip',
    WRAPPER: 'murphy-door-tooltip-wrapper',
    TOOLTIP: 'murphy-door-tooltip',
    VISIBLE: 'is-visible',
    TOP: 'tooltip-top',
    BOTTOM: 'tooltip-bottom',
    LEFT: 'tooltip-left',
    RIGHT: 'tooltip-right',
    TEXT: 'tooltip-text',
    HTML: 'tooltip-html',
  });

  // The tape-measure variant of the info icon ("Width", "Height", "Jamb
  // Depth", "Door Width", "Door Height" — see CONSTANTS.CATEGORY_TYPES
  // .MEASUREMENT_CATEGORIES). These are the only icons whose ACTIVATION is
  // handed off to the global "Will it fit?" wizard; their HOVER tooltip is
  // untouched. See handOffToWillItFit below.
  const MEASUREMENT_ICON_CLASS = 'murphy-door-info-icon--measurement';

  // Combined selector for all tooltip-enabled elements
  // Includes: info icons, button tooltips, forbidden-disabled elements, and gallery PTS badge
  const TOOLTIP_SELECTOR = `.${TOOLTIP_CLASSES.ICON}, .${TOOLTIP_CLASSES.BUTTON_TOOLTIP}, .forbidden-disabled, .murphy-door-configurator__pts-badge`;

  /**
   * The same trigger list, scoped to ONE category.
   *
   * 🚨 Never write `${TOOLTIP_SELECTOR}[data-category="x"]`. TOOLTIP_SELECTOR is a
   * comma-separated selector LIST, so an appended attribute filter binds only to
   * the final entry — the other three keep matching every element of their class
   * on the page, whatever category it belongs to.
   *
   * Measured live 2026-08-21: opening one tooltip stamped aria-expanded="true" on
   * all five info icons, each pointing at the same popup id. The idiom was already
   * present at three other call sites and was copied into a fourth before anyone
   * noticed, because with querySelector (singular) it usually returns a plausible
   * element and the damage is invisible.
   *
   * @param {string} categoryId
   * @returns {string} a selector list where EVERY entry carries the filter
   */
  /**
   * Element -> last-applied listener signature. A WeakMap so a trigger removed
   * from the DOM by a re-render is collected with its entry; this must never
   * become a retention surface (see the APO history-leak footgun in CLAUDE.md).
   */
  const wiredTriggers = new WeakMap();

  const triggerSelectorFor = (categoryId) => {
    const filter = `[data-category="${String(categoryId).replace(/"/g, '\\"')}"]`;
    return TOOLTIP_SELECTOR.split(',')
      .map((part) => part.trim() + filter)
      .join(', ');
  };

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[TooltipManager] Bootstrap complete event received');
        bootstrapComplete = true;

        // PATTERN 5: Event Replay Ready - process queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[TooltipManager] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[TooltipManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[TooltipManager] Subscribed to bootstrap event at module load time'
      );

    // Subscribe to material preference recomputation for tooltip content invalidation
    // When the preferred material SKU switches (e.g., Oak -> Walnut surviving contributor),
    // any cached tooltip text referencing the old preference is stale.
    // Invalidate the cache so tooltips re-fetch content on next display.
    const unsubMaterialRecomputed = CoreEventBus.subscribe(
      EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
      (event) => {
        if (!bootstrapComplete) return; // Respect bootstrap barrier

        const survivingWoodSku = event?.survivingWoodSku;
        const survivingFinishSku = event?.survivingFinishSku;

        isDev() &&
          console.log(
            `[TooltipManager] Material preferences recomputed (wood: ${survivingWoodSku || 'none'}, finish: ${survivingFinishSku || 'none'}) - invalidating tooltip cache`
          );

        // Mark cache as stale so it will be re-populated on next access
        cachePrePopulated = false;
        tooltipsData.clear();
        tooltipStates.clear();

        // Hide any currently visible tooltips since their content may be stale
        const visibleTooltips = document.querySelectorAll(
          `.${TOOLTIP_CLASSES.WRAPPER}.${TOOLTIP_CLASSES.VISIBLE}`
        );
        visibleTooltips.forEach((tooltip) => {
          hideTooltip(tooltip);
        });

        // Re-populate the cache with fresh data
        preCacheAllTooltipData();

        isDev() &&
          console.log(
            '[TooltipManager] Tooltip cache invalidated and re-populated after material preference recomputation'
          );
      }
    );
    cleanupHandlers.push(unsubMaterialRecomputed);
  }

  /**
   * Set conductor reference for CCP compliance
   * @param {Object} conductorInstance - The conductor instance
   */
  const setConductor = (conductorInstance) => {
    conductor = conductorInstance;
    isDev() &&
      console.log('[TooltipManager] CCP COMPLIANCE: conductor reference set');
  };

  /**
   * Get conductor reference with lazy initialization fallback
   * @returns {Object} Conductor instance
   */
  const getConductor = () => {
    if (!conductor) {
      // Use proper conductor hierarchy access
      if (
        window.MurphyDoorConfigurator &&
        window.MurphyDoorConfigurator.conductor
      ) {
        isDev() &&
          console.log(
            '[TooltipManager] Auto-setting conductor reference during lazy initialization'
          );
        setConductor(window.MurphyDoorConfigurator.conductor);
      }
    }
    return conductor;
  };

  /**
   * Detect if current device is mobile
   */
  const detectMobileDevice = () => {
    isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth < 768;
  };

  /**
   * Initialize Tooltip Manager
   *
   * PATTERN 3: 5-Stage Event Lifecycle (STARTED → VALIDATION → PROGRESS → SUCCESS/FAILED)
   * PATTERN 14: Try/Catch + Failed Events
   */
  const initialize = () => {
    // PATTERN 16: Initialization guard
    if (initialized) return;

    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(EVENTS.UI_TOOLTIP_MANAGER_INIT_REQUESTED, {
        module: 'TooltipManager',
        timestamp: Date.now(),
      });

      // Set mobile detection
      detectMobileDevice();

      // Set up window resize listener to update mobile detection
      window.addEventListener('resize', () => {
        detectMobileDevice();
      });

      // Set up click outside listener to close active tooltips
      document.addEventListener('click', handleDocumentClick);

      // Subscribe to system events via EventBus
      if (CoreEventBus) {
        // Pre-populate cache BEFORE navigation creates categories
        // Listen to the EARLY event that fires before structure creation
        const unsubPreStructure = CoreEventBus.subscribe(
          EVENTS.SYSTEM_PRE_STRUCTURE_INITIALIZATION,
          (eventData) => {
            isDev() &&
              console.log(
                '[TooltipManager] Received system:pre_structure_initialization event, pre-populating cache NOW'
              );
            // Set conductor reference from event data if available
            if (eventData && eventData.conductor) {
              setConductor(eventData.conductor);
            }
            preCacheAllTooltipData();
          }
        );
        cleanupHandlers.push(unsubPreStructure);

        // Also listen to system:initialized as backup (maintains compatibility)
        const unsubInitialized = CoreEventBus.subscribe(
          EVENTS.SYSTEM_INITIALIZED,
          preCacheAllTooltipData
        );
        cleanupHandlers.push(unsubInitialized);

        // Subscribe to UI events
        const unsubTooltipTriggered = CoreEventBus.subscribe(
          EVENTS.UI_TOOLTIP_TRIGGERED,
          handleTooltipEvent
        );
        cleanupHandlers.push(unsubTooltipTriggered);

        // Subscribe to category creation events to attach tooltip listeners
        const unsubCategoryCreated = CoreEventBus.subscribe(
          EVENTS.UI_CATEGORY_CREATED,
          handleCategoryCreated
        );
        cleanupHandlers.push(unsubCategoryCreated);

        // Subscribe to explicit tooltip initialization event
        // Coalesced: this event is published once per registration BURST, and a
        // single selection produces ~10 of those bursts (10 forbidden
        // re-validation passes). Running the full trigger walk 10x per click
        // re-attached the same stable handler references to the same elements
        // over and over — ~1,000 listener operations per selection for zero
        // behavioural difference, since addEventListener with an identical
        // (type, listener, capture) triple is a no-op by spec.
        const unsubInitTooltips = CoreEventBus.subscribe(
          EVENTS.UI_INITIALIZE_TOOLTIPS,
          scheduleTriggerAttach
        );
        cleanupHandlers.push(unsubInitTooltips);

        // Subscribe to custom tooltip registration events
        const unsubCustomTooltip = CoreEventBus.subscribe(
          EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
          handleCustomTooltipRegistration
        );
        cleanupHandlers.push(unsubCustomTooltip);

        // Clean up any tooltips that were open before entering fullscreen.
        // This prevents stale PDP tooltips from being trapped under the fullscreen overlay.
        // CONSTANTS.EVENTS.UI_FULLSCREEN_ENTERED — aliased below as EVENTS.UI_FULLSCREEN_ENTERED.
        // Tooltips triggered *within* fullscreen render correctly at z-index: 9999
        // (exceeds fullscreen overlay z-index: 1000 and close button z-index: 1001).
        cleanupHandlers.push(
          CoreEventBus.subscribe(EVENTS.UI_FULLSCREEN_ENTERED, () => {
            if (!bootstrapComplete) return;
            // Reset tracked tooltip states and hide any visible tooltip DOM nodes
            tooltipStates.forEach((isOpen, categoryId) => {
              if (isOpen) tooltipStates.set(categoryId, false);
            });
            const visibleTooltips = document.querySelectorAll(
              `.${TOOLTIP_CLASSES.WRAPPER}.${TOOLTIP_CLASSES.VISIBLE}`
            );
            visibleTooltips.forEach((tooltip) => hideTooltip(tooltip));
            isDev() &&
              console.log(
                '[TooltipManager] Fullscreen entered — all tooltips hidden'
              );
          })
        );

        // Reset tooltip state on fullscreen exit so PDP mode starts with clean state
        cleanupHandlers.push(
          CoreEventBus.subscribe(EVENTS.UI_FULLSCREEN_EXITED, () => {
            if (!bootstrapComplete) return;
            tooltipStates.forEach((_, categoryId) =>
              tooltipStates.set(categoryId, false)
            );
            isDev() &&
              console.log(
                '[TooltipManager] Fullscreen exited — tooltip states reset'
              );
          })
        );

        // STAGE 4: SUCCESS - Publish ready event
        const duration = performance.now() - startTime;
        trackPerformance('TOOLTIP_DISPLAY', duration);

        publishEvent(EVENTS.UI_TOOLTIP_MANAGER_READY, {
          module: 'TooltipManager',
          duration,
          timestamp: Date.now(),
        });
      }

      initialized = true;
    } catch (error) {
      // STAGE 5: FAILED
      const duration = performance.now() - startTime;

      publishEvent(EVENTS.UI_TOOLTIPS_INITIALIZATION_FAILED, {
        error: error.message,
        duration,
        timestamp: Date.now(),
      });

      // Route through centralized error handler (DRY: single publication point)
      window.MurphyDoorConfigurator.utils.handleSystemError({
        type: 'TooltipError',
        error: error,
        source: 'tooltipManager.initialize',
        recoverable: true,
        impact: 'low',
      });
    }
  };

  /**
   * Initialize any tooltip icons that already exist in the DOM
   * This is crucial for tooltips that were rendered before our module was initialized
   * NOTE: This function only sets up DOM listeners, NOT data fetching
   * Data is pre-cached by preCacheAllTooltipData() before this runs
   */
  const initializeExistingTooltips = () => {
    try {
      // ONLY set up listeners for existing tooltip icons (NO data fetching!)
      const existingIcons = document.querySelectorAll(TOOLTIP_SELECTOR);

      existingIcons.forEach((icon) => {
        setupTooltipEventListenersForIcon(icon);
      });
    } catch (error) {
      isDev() &&
        console.warn(
          '[TooltipManager] Error initializing existing tooltips:',
          error
        );
    }
  };

  /**
   * Coalesced replacement for the per-registration trigger lookup.
   *
   * 🔥 WHY THIS EXISTS — measured, not theorised (2026-09-04)
   *
   * handleCustomTooltipRegistration() used to end with a document-wide
   * `querySelector(triggerSelectorFor(categoryId))` and attach listeners to the
   * one icon it found. That is fine for ONE registration. It is not fine for the
   * way this event is actually published: the forbidden-tooltip initializer and
   * the ui-updater both publish UI_REGISTER_CUSTOM_TOOLTIP inside a
   * `itemsToDisable.forEach(...)` loop, once per forbidden variant.
   *
   * Profiled on production (unminified assets swapped in, mobile emulation,
   * /products/flush-mount-bookcase-door-2-0):
   *
   *   ui:register_custom_tooltip .............. 926 publishes PER SELECTION
   *     = 91 forbidden variants x ~10 forbidden re-validation passes
   *   handleCustomTooltipRegistration querySelector time ... 1,439ms / 6 clicks
   *     = ~240ms of main-thread BLOCKING per selection, 61.5% of all DOM-query
   *       time on the page and ~25% of every sampled millisecond
   *
   * Each of those 926 calls is a document-wide scan for a FOUR-part comma
   * selector list. The customer feels it as the device heating up over a long
   * configuration session — the second half of the QA "device gets hot" finding.
   *
   * The work was also redundant: every burst publisher follows its loop with
   * UI_INITIALIZE_TOOLTIPS, whose handler already walks EVERY trigger in the
   * document and attaches to all of them — a strict superset of the single icon
   * the per-registration lookup could find.
   *
   * 🚨 It is NOT universally redundant, which is why this is a coalescer and not
   * a deletion. `refreshTooltipContent()` in the pass-through initializer
   * re-publishes registrations with NO following UI_INITIALIZE_TOOLTIPS. Simply
   * dropping the lookup would have left that path un-attached. Scheduling a
   * microtask pass covers it — and covers any FUTURE caller that forgets the
   * initialize event, which the old code did not.
   *
   * A microtask (not rAF, not setTimeout) is deliberate: user input cannot be
   * delivered before the microtask checkpoint drains, so no listener can ever be
   * missing at the moment a customer actually interacts. rAF is paused in
   * backgrounded tabs (see the quick-config footgun in CLAUDE.md) and would have
   * stranded attachment there.
   */
  let attachPassScheduled = false;

  const runCoalescedAttachPass = () => {
    attachPassScheduled = false;
    initializeExistingTooltips();
  };

  const scheduleTriggerAttach = () => {
    if (attachPassScheduled) return;
    attachPassScheduled = true;
    Promise.resolve().then(runCoalescedAttachPass);
  };

  /**
   * Schedule tooltip hide with debounce delay (DRY helper)
   * Consolidates hide scheduling logic used in multiple event handlers
   * @param {string} categoryId - Category identifier for the tooltip
   * @param {HTMLElement} tooltipWrapper - The tooltip wrapper element to hide
   */
  const scheduleTooltipHide = (categoryId, tooltipWrapper) => {
    // Clear any existing hide timeout to reset the timer
    if (hideTimeouts.has(categoryId)) {
      clearTimeout(hideTimeouts.get(categoryId));
    }

    // Set new hide timeout
    const timeoutId = setTimeout(() => {
      hideTooltip(tooltipWrapper);
      hideTimeouts.delete(categoryId);
    }, HIDE_DELAY_MS);

    hideTimeouts.set(categoryId, timeoutId);
  };

  /**
   * Extract text from a nested JSON tooltip structure
   * @param {Object} tooltipObj - The tooltip object with nested structure
   * @returns {string} Extracted text content
   */
  const extractTextFromNestedTooltip = (tooltipObj) => {
    try {
      if (!tooltipObj || typeof tooltipObj !== 'object') {
        return '';
      }

      // Handle case where it's already a string
      if (typeof tooltipObj === 'string') {
        return tooltipObj;
      }

      // Handle nested JSON structure like the one from Shopify's rich text
      if (tooltipObj.type === 'root' && Array.isArray(tooltipObj.children)) {
        return tooltipObj.children
          .filter((node) => node && typeof node === 'object') // Make sure node is valid
          .map((node) => {
            if (node.type === 'paragraph' && Array.isArray(node.children)) {
              return node.children
                .filter(
                  (child) =>
                    child &&
                    typeof child === 'object' &&
                    child.type === 'text' &&
                    child.value
                )
                .map((child) => child.value)
                .join(' ');
            }
            return '';
          })
          .filter((text) => text && text.trim() !== '')
          .join(' ');
      }

      // For any other object structure, attempt to extract string values
      if (typeof tooltipObj === 'object') {
        // Try to find any string properties that might contain tooltip text
        const possibleTextValues = Object.values(tooltipObj)
          .filter((val) => typeof val === 'string' && val.trim() !== '')
          .join(' ');

        if (possibleTextValues) {
          return possibleTextValues;
        }
      }

      return '';
    } catch (error) {
      return '';
    }
  };

  /**
   * Pre-populate tooltip cache to avoid conductor hits during HTML generation
   * This runs ONCE on initialization before any UI rendering happens
   *
   * PATTERN 3: 5-Stage Event Lifecycle (STARTED → PROGRESS → SUCCESS/FAILED)
   * PATTERN 14: Try/Catch + Failed Events
   */
  const preCacheAllTooltipData = () => {
    // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
    if (!bootstrapComplete) {
      isDev() &&
        console.log(
          '[TooltipManager] preCacheAllTooltipData queued - bootstrap not complete'
        );
      pendingCalls.push({
        fn: preCacheAllTooltipData,
        context: null,
        args: [],
      });
      return;
    }

    if (cachePrePopulated) {
      isDev() &&
        console.log('[TooltipManager] Cache already pre-populated, skipping');
      return;
    }

    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(EVENTS.UI_TOOLTIP_CACHE_STARTED, {
        timestamp: Date.now(),
      });

      isDev() &&
        console.log(
          '[TooltipManager] PERFORMANCE: pre-populating tooltip cache'
        );

      // Access config data directly from DOM (static configuration metadata)
      const configDataElement = document.getElementById(
        'murphy-door-config-data'
      );
      if (!configDataElement) {
        isDev() &&
          console.warn('[TooltipManager] Config data element not found');
        return;
      }

      const configData = JSON.parse(configDataElement.textContent);

      if (configData && configData.tabCategories) {
        let cachedCount = 0;

        configData.tabCategories.forEach((category) => {
          if (category.category_tooltip || category.category_tooltip_html) {
            // Process tooltip text - handle nested JSON structure
            let tooltipText = '';
            if (category.category_tooltip) {
              if (typeof category.category_tooltip === 'object') {
                tooltipText = extractTextFromNestedTooltip(
                  category.category_tooltip
                );
              } else {
                tooltipText = category.category_tooltip;
              }
            }

            // Cache tooltip data
            tooltipsData.set(category.category, {
              text: tooltipText || '',
              html: category.category_tooltip_html || '',
            });

            // Initialize tooltip state
            tooltipStates.set(category.category, false);
            cachedCount++;
          }
        });

        cachePrePopulated = true;

        // STAGE 4: SUCCESS
        const duration = performance.now() - startTime;
        trackPerformance('CACHE_POPULATION', duration);

        publishEvent(EVENTS.UI_TOOLTIP_CACHE_SUCCESS, {
          cachedCount,
          duration,
          timestamp: Date.now(),
        });

        isDev() &&
          console.log(
            `[TooltipManager] PERFORMANCE: cached ${cachedCount} tooltips in ${duration.toFixed(1)}ms`
          );

        // Now set up DOM listeners for any existing tooltip icons
        initializeExistingTooltips();
      }
    } catch (error) {
      // STAGE 5: FAILED
      const duration = performance.now() - startTime;

      publishEvent(EVENTS.UI_TOOLTIP_CACHE_FAILED, {
        error: error.message,
        duration,
        timestamp: Date.now(),
      });

      isDev() &&
        console.error('[TooltipManager] Failed to pre-populate cache:', error);
      // Route through centralized error handler (DRY: single publication point)
      window.MurphyDoorConfigurator.utils.handleSystemError({
        type: 'TooltipError',
        error: error,
        source: 'tooltipManager.preCacheAllTooltipData',
        recoverable: true,
        impact: 'low',
      });
    }
  };

  /**
   * Handle tooltip hover event
   * @param {Event} event - Mouse hover event
   */
  const handleTooltipHover = (event) => {
    // Only for desktop devices
    if (isMobileDevice) return;

    const icon = event.currentTarget;
    const categoryId = icon.getAttribute('data-category');

    if (!categoryId || !tooltipsData.has(categoryId)) return;

    // Don't show on hover if tooltip is already open via click
    if (isTooltipOpen(categoryId)) return;

    // UX ENHANCEMENT: Cancel any pending hide timeout for this tooltip
    // This prevents flickering when user quickly re-hovers
    if (hideTimeouts.has(categoryId)) {
      clearTimeout(hideTimeouts.get(categoryId));
      hideTimeouts.delete(categoryId);
    }

    showTooltip(icon, categoryId);

    // PATTERN 10: UI Update Events
    publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
      categoryId,
      action: 'hover',
      timestamp: Date.now(),
    });
  };

  /**
   * Handle tooltip mouse out event
   * UX ENHANCEMENT: Debounced hide with 250ms delay to prevent flickering
   * @param {Event} event - Mouse out event
   */
  const handleTooltipMouseOut = (event) => {
    // Only for desktop devices
    if (isMobileDevice) return;

    const icon = event.currentTarget;
    const categoryId = icon.getAttribute('data-category');

    if (!categoryId) return;

    // Don't hide on mouseout if tooltip was opened via click
    if (isTooltipOpen(categoryId)) return;

    // Find the tooltip element
    const tooltipWrapper = document.querySelector(
      `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
    );

    if (tooltipWrapper) {
      // UX ENHANCEMENT: Debounced hide with delay
      // This prevents flickering when moving between nested elements
      // or when user quickly re-hovers the trigger element
      scheduleTooltipHide(categoryId, tooltipWrapper);
    }
  };

  /**
   * Does `icon` STILL own a registered tooltip?
   *
   * One definition, because two callers depend on it agreeing: toggleTooltipFor
   * (which returns null when it does not) and the click handler, which has to
   * answer the question BEFORE it may call event.stopPropagation(). Listeners
   * are only ever bound to elements that had a data-category at bind time, but
   * an element can lose one later — the Swing gate's In-Swing option registers
   * a tooltip while blocked and deregisters when it becomes selectable again —
   * and a stale listener that keeps swallowing clicks makes that option
   * permanently unpickable. See the long note in handleTooltipClick.
   *
   * @param {Element} icon
   * @returns {string|null} the categoryId, or null if this element owns no tooltip
   */
  const registeredTooltipCategory = (icon) => {
    const categoryId = icon && icon.getAttribute('data-category');
    return categoryId && tooltipsData.has(categoryId) ? categoryId : null;
  };

  /**
   * Toggle the tooltip owned by `icon`.
   *
   * Extracted from handleTooltipClick so the keyboard path can reuse it verbatim.
   * The keyboard path MUST call this directly and MUST NOT synthesize icon.click():
   * these icons live inside the category card, and a click that escapes the icon
   * reaches the card handler in md-config-core-event-handler.js, which in apo_style
   * resets the whole card and discards the customer's selection for that category.
   *
   * @param {Element} icon - the trigger element carrying data-category
   * @returns {string|null} the categoryId toggled, or null if the icon owns no tooltip
   */
  const toggleTooltipFor = (icon) => {
    const categoryId = registeredTooltipCategory(icon);

    if (!categoryId) return null;

    // Get existing tooltip
    const existingTooltip = document.querySelector(
      `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
    );

    // Toggle tooltip state based on current state
    const currentState = isTooltipOpen(categoryId);

    if (currentState) {
      // If already open via click, close it
      if (existingTooltip) {
        hideTooltip(existingTooltip);
      }
      setTooltipState(categoryId, false);
    } else {
      // If closed, open it
      // First hide any other open tooltips
      tooltipStates.forEach((isOpen, catId) => {
        if (isOpen && catId !== categoryId) {
          const tooltip = document.querySelector(
            `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${catId}"]`
          );
          if (tooltip) {
            hideTooltip(tooltip);
          }
          setTooltipState(catId, false);
        }
      });

      // Show this tooltip
      showTooltip(icon, categoryId);
      setTooltipState(categoryId, true);
    }

    return categoryId;
  };

  /**
   * Hand a "how to measure" icon's ACTIVATION to the global "Will it fit?"
   * wizard instead of toggling its tooltip.
   *
   * Only the tape-measure icons (.murphy-door-info-icon--measurement) do this.
   * HOVER IS DELIBERATELY UNTOUCHED — handleTooltipHover/handleTooltipMouseOut
   * never reach this function, so the desktop hover tooltip that already works
   * keeps working exactly as it does today. This changes what a CLICK (and its
   * keyboard equivalent) means, nothing else.
   *
   * Why a direct method call rather than the widget's own document-level
   * [data-willitfit-open] delegation: the caller must stopPropagation(), or
   * the click escapes the icon and reaches the category card handler in
   * md-config-core-event-handler.js. That same stopPropagation would prevent
   * a bubble-phase document listener from ever seeing the event, so tagging
   * the icon with data-willitfit-open would produce a dead click.
   *
   * 🚨 CAPABILITY, NOT PRESENCE. `document.querySelector('will-it-fit')` is
   * truthy even when assets/willitfit.js never loaded — the element is then a
   * bare HTMLElement with no behaviour. That is precisely the trap that
   * silently ate add-to-cart in the cart-drawer incident (CLAUDE.md). Probing
   * for the method means a non-upgraded element falls through and the icon
   * keeps its old tooltip toggle rather than becoming a dead tap.
   *
   * The wizard reads the page's mount type (Flush vs Surface, from
   * product.tags) itself and skips its own first step — this module never
   * touches raw Shopify data, per the Phase 2 CCP rule.
   *
   * @param {Element} icon - the trigger carrying data-category
   * @param {string|null} categoryId
   * @returns {boolean} true when the wizard took the interaction
   */
  const handOffToWillItFit = (icon, categoryId) => {
    if (
      !icon ||
      !icon.classList ||
      !icon.classList.contains(MEASUREMENT_ICON_CLASS)
    ) {
      return false;
    }

    const wizard = document.querySelector('will-it-fit');
    if (!wizard || typeof wizard.openExternally !== 'function') return false;

    // A desktop hover almost always left this tooltip open under the pointer.
    // Drop it before the modal covers it, so it is not still sitting there
    // when the customer closes the wizard and focus returns to this icon.
    if (categoryId) {
      const openWrapper = document.querySelector(
        `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
      );
      if (openWrapper) hideTooltip(openWrapper);
      setTooltipState(categoryId, false);
    }

    return (
      wizard.openExternally({
        opener: icon,
        source: 'configurator',
        label: categoryId || 'how to measure',
      }) === true
    );
  };

  /**
   * Handle activation of a tooltip trigger by pointer.
   * @param {Event} event - Click event
   */
  const handleTooltipClick = (event) => {
    // stopPropagation AFTER confirming this element actually has a tooltip.
    //
    // It used to be the first line, unconditionally, which meant that once these
    // listeners were bound to an element they swallowed every click on it for
    // the rest of the page's life -- including after its tooltip was
    // deregistered. Listeners are only ever attached to elements that had a
    // data-category at bind time, but an element can lose that later.
    //
    // Live symptom 2026-08-24: the Swing gate's In-Swing option registers a
    // tooltip while it is blocked (explaining why), then deregisters when it
    // becomes selectable again. The listener stayed, kept stopping propagation,
    // and the delegated set-gate-answer handler never saw the click -- so once
    // In-Swing had been blocked once it could never be chosen again, while
    // Out-Swing kept working. Reported by Daniel: "cant go back and change swing
    // direction only hinge side".
    //
    // registeredTooltipCategory answers that question, and returns null for
    // exactly the case that must fall through to whatever else is listening.
    // It runs FIRST — ahead of every stopPropagation() below, including the
    // measurement branch's — so the deregistered-listener bug above cannot
    // come back through a new code path.
    const categoryId = registeredTooltipCategory(event.currentTarget);
    if (!categoryId) return;

    event.stopPropagation();

    // 🚨 THE DEAD CLICK IS SWALLOWED HERE — announce it before it disappears.
    //
    // md-config-ui-updater.js gives every forbidden tile BOTH a visual disabled
    // class AND a tooltip (the tooltip is what explains the refusal), so a
    // greyed tile is itself a tooltip trigger. This listener is bound AT-TARGET
    // and the stopPropagation() above is unconditional once a tooltip is
    // registered, so the click never reaches the document-level delegated
    // forbidden guards in md-config-core-event-handler.js. Verified live
    // 2026-09-18 on the 6-9/16 jamb (product tile) and Door Depth 12" (variant
    // tile): three clicks each, zero bus events. Those guards still exist and
    // still matter — they cover a tile refused before its tooltip is bound —
    // but on a settled page THIS is the path a real dead click takes.
    //
    // Announce-only: behaviour below is untouched, the tooltip still opens.
    try {
      const trigger = event.currentTarget;
      const tile =
        trigger.closest('[data-variant-id],[data-product-id]') || trigger;
      const isForbidden = [trigger, tile].some(
        (el) =>
          el &&
          el.classList &&
          (el.classList.contains('forbidden-disabled') ||
            el.classList.contains('forbidden-product-all-disabled'))
      );
      if (isForbidden) {
        // `categoryId` above is the tooltip's OWN key, which for a forbidden
        // tile is synthetic — ui-updater writes 'forbidden-<variantId>' or
        // 'forbidden-product-<productId>'. Never report that: it is unbounded
        // cardinality and is not a configurator category. Walk past it to the
        // real one on an ancestor.
        const ownCategory = tile.getAttribute('data-category') || '';
        const realCategory = /^forbidden(-product)?-/.test(ownCategory)
          ? tile.parentElement
              ?.closest('[data-category]')
              ?.getAttribute('data-category') || null
          : ownCategory ||
            tile.closest('[data-category]')?.getAttribute('data-category') ||
            null;

        publishEvent(EVENTS.UI_FORBIDDEN_TILE_CLICK_REFUSED, {
          categoryId: realCategory,
          variantId: tile.getAttribute('data-variant-id') || null,
          productId: tile.getAttribute('data-product-id') || null,
          // Tooltip TEXT is never forwarded — it is customer-facing free text.
          reason: 'forbidden_tooltip_click',
          timestamp: Date.now(),
        });
      }
    } catch (error) {
      // Telemetry must never break a tooltip.
      isDev() &&
        console.warn('[TooltipManager] forbidden-click report failed:', error);
    }

    // The measurement ("how to measure") icons opt OUT of the toggle below:
    // their click opens the "Will it fit?" wizard instead. Hover is untouched.
    if (handOffToWillItFit(event.currentTarget, categoryId)) {
      publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
        categoryId,
        action: 'willitfit_handoff',
        isOpen: false,
        timestamp: Date.now(),
      });
      return;
    }

    toggleTooltipFor(event.currentTarget);

    // PATTERN 10: UI Update Events
    publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
      categoryId,
      action: 'click',
      isOpen: isTooltipOpen(categoryId),
      timestamp: Date.now(),
    });
  };

  /**
   * Handle activation of a tooltip trigger by keyboard (WCAG 2.1.1, 1.4.13).
   *
   * These triggers ship role="button" tabindex="0", so assistive tech promises the
   * customer they are operable. Until 2026-08-21 nothing listened for a key on them:
   * they were focusable, announced as buttons, and completely inert. Escape is here
   * for 1.4.13's "dismissable" requirement — the tooltip is otherwise only closable
   * by pointing somewhere else.
   *
   * @param {KeyboardEvent} event
   */
  const handleTooltipKeydown = (event) => {
    const icon = event.currentTarget;

    if (event.key === 'Escape') {
      const categoryId = icon.getAttribute('data-category');
      if (!categoryId || !isTooltipOpen(categoryId)) return;

      const openTooltip = document.querySelector(
        `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
      );
      if (openTooltip) hideTooltip(openTooltip);
      setTooltipState(categoryId, false);

      publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
        categoryId,
        action: 'keyboard_dismiss',
        isOpen: false,
        timestamp: Date.now(),
      });
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    // Space scrolls the page by default, and both keys would otherwise bubble to the
    // category card. Stop them here, on the icon, rather than filtering downstream.
    event.preventDefault();
    event.stopPropagation();

    // Keyboard parity with the pointer path — see handOffToWillItFit.
    const measureCategoryId = icon.getAttribute('data-category');
    if (handOffToWillItFit(icon, measureCategoryId)) {
      publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
        categoryId: measureCategoryId,
        action: 'willitfit_handoff',
        isOpen: false,
        timestamp: Date.now(),
      });
      return;
    }

    const categoryId = toggleTooltipFor(icon);
    if (!categoryId) return;

    publishEvent(EVENTS.UI_TOOLTIP_TRIGGERED, {
      categoryId,
      action: 'keyboard',
      isOpen: isTooltipOpen(categoryId),
      timestamp: Date.now(),
    });
  };

  /**
   * Handle document click to close tooltips when clicking outside
   * @param {Event} event - Click event
   */
  const handleDocumentClick = (event) => {
    // Ignore if clicked on a tooltip icon or within it
    if (event.target.closest(TOOLTIP_SELECTOR)) return;

    // Close all visible tooltips if clicked outside
    const visibleTooltips = document.querySelectorAll(
      `.${TOOLTIP_CLASSES.WRAPPER}.${TOOLTIP_CLASSES.VISIBLE}`
    );

    if (visibleTooltips.length > 0) {
      visibleTooltips.forEach((tooltip) => {
        // Check if click is outside the tooltip
        if (!tooltip.contains(event.target)) {
          // Get category ID to update state
          const categoryId = tooltip.getAttribute('data-category');

          hideTooltip(tooltip);

          // Update state to closed
          if (categoryId) {
            setTooltipState(categoryId, false);
          }
        }
      });
    }
  };

  /**
   * Handle tooltip events from event bus
   * PATTERN 4: Event Consumer Handler
   * @param {Object} eventData - Event data
   */
  const handleTooltipEvent = (eventData) => {
    if (!eventData || !eventData.categoryId) return;

    const { categoryId, action } = eventData;

    // Find the tooltip icon
    const tooltipIcon = document.querySelector(
      triggerSelectorFor(categoryId)
    );

    if (!tooltipIcon) return;

    if (action === 'show') {
      showTooltip(tooltipIcon, categoryId);
    } else if (action === 'hide') {
      const tooltipWrapper = document.querySelector(
        `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
      );
      if (tooltipWrapper) {
        hideTooltip(tooltipWrapper);
      }
    }
  };

  /**
   * Show tooltip for a specific category
   *
   * PATTERN 3: 5-Stage Event Lifecycle (STARTED → SUCCESS/FAILED)
   * PATTERN 14: Try/Catch + Failed Events
   * PATTERN 17: XSS Prevention - HTML content from trusted Shopify admin metafields only;
   *             text content uses safe textContent assignment; innerHTML wrapped with sanitizeHTML()
   *
   * @param {HTMLElement} icon - The tooltip icon element
   * @param {string} categoryId - Category ID
   */
  const showTooltip = (icon, categoryId) => {
    const startTime = performance.now();

    try {
      if (!tooltipsData.has(categoryId)) {
        return;
      }

      // STAGE 1: START
      publishEvent(EVENTS.UI_TOOLTIP_DISPLAY_STARTED, {
        categoryId,
        timestamp: Date.now(),
      });

      // Get tooltip data
      const tooltipData = tooltipsData.get(categoryId);

      // Check if tooltip already exists
      let tooltipWrapper = document.querySelector(
        `.${TOOLTIP_CLASSES.WRAPPER}[data-category="${categoryId}"]`
      );

      if (!tooltipWrapper) {
        // Create new tooltip
        tooltipWrapper = document.createElement('div');
        tooltipWrapper.className = TOOLTIP_CLASSES.WRAPPER;
        tooltipWrapper.setAttribute('data-category', categoryId);

        // Optional presentation variant, supplied by whoever REGISTERED the
        // tooltip. Styling then follows the CONTENT TYPE rather than the
        // category id.
        //
        // 🚨 Why this exists. The pass-through tooltip was styled with
        // `[data-category='pass-through-space']`, but snippets/
        // pass-through-space-info-display.liquid is deliberately reusable and
        // is also rendered as 'door-pocket-depth'. That second instance
        // therefore missed the light-card styling entirely and rendered dark
        // red on the dark default bubble — the same content, illegible.
        // Keying off a category VALUE means every new caller of a shared
        // snippet silently opts out of its own styling.
        if (
          tooltipData.variantClass &&
          typeof tooltipData.variantClass === 'string'
        ) {
          // Space-separated tokens, filtered: this string reaches classList
          // and must not be able to smuggle anything else in.
          tooltipData.variantClass
            .split(/\s+/)
            .filter((token) => /^[a-zA-Z0-9_-]+$/.test(token))
            .forEach((token) => tooltipWrapper.classList.add(token));
        }

        // WCAG 4.1.2: give the popup a role and a stable id so the trigger can
        // point at it with aria-describedby. Until 2026-08-21 the keyboard path
        // opened a tooltip that was announced to nobody -- the button worked, and
        // the result of pressing it was invisible to exactly the people the
        // keyboard fix was for. The id is derived from the categoryId, which is
        // already unique per tooltip and survives re-creation.
        tooltipWrapper.setAttribute('role', 'tooltip');
        tooltipWrapper.id = tooltipDomId(categoryId);

        // Determine tooltip direction (top by default)
        tooltipWrapper.classList.add(TOOLTIP_CLASSES.TOP);

        // Create tooltip content
        const tooltip = document.createElement('div');
        tooltip.className = TOOLTIP_CLASSES.TOOLTIP;

        // Add content based on type
        // PATTERN 17: XSS Prevention
        // HTML tooltips: sourced from Shopify admin metafields (trusted CMS content)
        // Text tooltips: use textContent (inherently XSS-safe)
        if (
          tooltipData.html &&
          typeof tooltipData.html === 'string' &&
          tooltipData.html.trim() !== ''
        ) {
          tooltip.classList.add(TOOLTIP_CLASSES.HTML);
          // XSS-SAFE: sanitizeHTML wrapper confirms content is from trusted Shopify admin metafield
          // Source: category_tooltip_html metafield (admin-authored rich content with videos/formatting)
          tooltip.innerHTML = sanitizeHTML(tooltipData.html);

          // Handle videos in tooltips by making them pause when tooltip is hidden
          const videos = tooltip.querySelectorAll('video');
          if (videos.length > 0) {
            videos.forEach((video) => {
              // Make sure videos autoplay when tooltip is shown
              video.setAttribute('autoplay', 'true');
              video.setAttribute('muted', 'true');
              video.setAttribute('playsinline', 'true');

              // If iOS, we need to handle autoplay manually
              // Uses global window.isIOS set in theme.liquid
              if (window.isIOS) {
                setTimeout(() => {
                  video.muted = true;
                  video.play().catch(() => {});
                }, 100);
              }
            });
          }
        } else if (
          tooltipData.text &&
          typeof tooltipData.text === 'string' &&
          tooltipData.text.trim() !== ''
        ) {
          tooltip.classList.add(TOOLTIP_CLASSES.TEXT);
          // PATTERN 17: Safe textContent assignment (no XSS vector)
          tooltip.textContent = tooltipData.text;
        } else if (tooltipData.text && typeof tooltipData.text === 'object') {
          // Handle case where text is an object (nested JSON structure)
          const extractedText = extractTextFromNestedTooltip(tooltipData.text);

          if (extractedText && extractedText.trim() !== '') {
            tooltip.classList.add(TOOLTIP_CLASSES.TEXT);
            // PATTERN 17: Safe textContent assignment (no XSS vector)
            tooltip.textContent = extractedText;

            // Update tooltipData with the extracted text for future use
            tooltipsData.set(categoryId, {
              text: extractedText,
              html: tooltipData.html || '',
            });
          } else {
            return; // Don't show empty tooltips
          }
        } else {
          return; // Don't show empty tooltips
        }

        tooltipWrapper.appendChild(tooltip);

        // Append tooltip to the document body for better positioning and to avoid clipping
        document.body.appendChild(tooltipWrapper);

        // UX ENHANCEMENT: Make tooltip itself hoverable
        // This keeps the tooltip visible when user hovers over it
        // Prevents flickering when moving cursor from trigger to tooltip
        tooltipWrapper.addEventListener('mouseenter', () => {
          // Cancel any pending hide timeout
          if (hideTimeouts.has(categoryId)) {
            clearTimeout(hideTimeouts.get(categoryId));
            hideTimeouts.delete(categoryId);
          }
        });

        tooltipWrapper.addEventListener('mouseleave', () => {
          // Start hide timer when leaving tooltip
          if (!isTooltipOpen(categoryId)) {
            scheduleTooltipHide(categoryId, tooltipWrapper);
          }
        });
      } else {
        // Tooltip already exists - cancel any pending hide
        if (hideTimeouts.has(categoryId)) {
          clearTimeout(hideTimeouts.get(categoryId));
          hideTimeouts.delete(categoryId);
        }
      }

      // Position the tooltip
      positionTooltipAbsolute(tooltipWrapper, icon);

      // Show the tooltip
      tooltipWrapper.classList.add(TOOLTIP_CLASSES.VISIBLE);

      // Track active tooltip
      activeTooltip = tooltipWrapper;

      // STAGE 4: SUCCESS
      const duration = performance.now() - startTime;
      trackPerformance('TOOLTIP_DISPLAY', duration);

      publishEvent(EVENTS.UI_TOOLTIP_DISPLAY_SUCCESS, {
        categoryId,
        duration,
        timestamp: Date.now(),
      });
    } catch (error) {
      // STAGE 5: FAILED
      const duration = performance.now() - startTime;

      publishEvent(EVENTS.UI_TOOLTIP_DISPLAY_FAILED, {
        categoryId,
        error: error.message,
        duration,
        timestamp: Date.now(),
      });

      // Route through centralized error handler (DRY: single publication point)
      window.MurphyDoorConfigurator.utils.handleSystemError({
        type: 'TooltipError',
        error: error,
        source: 'tooltipManager.showTooltip',
        recoverable: true,
        impact: 'low',
      });
    }
  };

  /**
   * Position the tooltip with absolute positioning relative to the icon
   * @param {HTMLElement} tooltipWrapper - The tooltip wrapper element
   * @param {HTMLElement} iconElement - The icon element
   */
  const positionTooltipAbsolute = (tooltipWrapper, iconElement) => {
    try {
      // Reset position classes
      [
        TOOLTIP_CLASSES.TOP,
        TOOLTIP_CLASSES.BOTTOM,
        TOOLTIP_CLASSES.LEFT,
        TOOLTIP_CLASSES.RIGHT,
      ].forEach((cls) => {
        tooltipWrapper.classList.remove(cls);
      });

      // Get icon position
      const iconRect = iconElement.getBoundingClientRect();
      const tooltipRect = tooltipWrapper.getBoundingClientRect();

      // Determine the best position based on available space
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Default to top if possible
      let position = TOOLTIP_CLASSES.TOP;

      // Check if there's enough space above
      if (iconRect.top < tooltipRect.height + 20) {
        position = TOOLTIP_CLASSES.BOTTOM;
      }

      // Mobile devices always show below
      if (isMobileDevice || windowWidth < 768) {
        position = TOOLTIP_CLASSES.BOTTOM;
      }

      // Apply the position class
      tooltipWrapper.classList.add(position);

      // Set absolute positioning
      tooltipWrapper.style.position = 'fixed';

      // For video tooltips, we want them centered on screen on mobile
      const isVideoTooltip = tooltipWrapper.querySelector('video') !== null;
      const isMobile = windowWidth < 768;

      if (isVideoTooltip && isMobile) {
        // Center on screen for videos on mobile
        tooltipWrapper.style.left = '50%';
        tooltipWrapper.style.transform = 'translateX(-50%)';
        tooltipWrapper.style.top = '50%';
        tooltipWrapper.style.bottom = 'auto';
        tooltipWrapper.style.maxWidth = 'calc(100vw - 40px)';
        tooltipWrapper.style.width = '100%';
        return;
      }

      // Position vertically - this code stays the same
      if (position === TOOLTIP_CLASSES.TOP) {
        tooltipWrapper.style.bottom = `${windowHeight - iconRect.top + 5}px`;
        tooltipWrapper.style.top = 'auto';
      } else if (position === TOOLTIP_CLASSES.BOTTOM) {
        tooltipWrapper.style.top = `${iconRect.bottom + 5}px`;
        tooltipWrapper.style.bottom = 'auto';
      }

      // Position horizontally with smart arrow positioning
      // Calculate original icon center position for arrow alignment
      const iconCenterX = iconRect.left + iconRect.width / 2;
      const tooltipHalfWidth = tooltipRect.width / 2;

      // Calculate ideal tooltip left position (centered on icon)
      let tooltipLeftPosition = iconCenterX - tooltipHalfWidth;
      let arrowOffset = 0; // Offset from center (0 = perfectly centered)

      // Check for screen edge collisions and adjust
      if (tooltipLeftPosition < 10) {
        // Too close to left edge - shift tooltip right
        const newTooltipLeft = 10;
        // Calculate how far the arrow needs to slide left to still point at icon
        arrowOffset = iconCenterX - (newTooltipLeft + tooltipHalfWidth);
        tooltipLeftPosition = newTooltipLeft;
      } else if (tooltipLeftPosition + tooltipRect.width > windowWidth - 10) {
        // Too close to right edge - shift tooltip left
        const newTooltipLeft = windowWidth - tooltipRect.width - 10;
        // Calculate how far the arrow needs to slide right to still point at icon
        arrowOffset = iconCenterX - (newTooltipLeft + tooltipHalfWidth);
        tooltipLeftPosition = newTooltipLeft;
      }

      // Apply positioning WITHOUT transform (we're calculating absolute position directly)
      tooltipWrapper.style.left = `${tooltipLeftPosition}px`;
      tooltipWrapper.style.transform = 'none';

      // Set CSS custom property for arrow offset (used by CSS ::before pseudo-element)
      tooltipWrapper.style.setProperty('--arrow-offset', `${arrowOffset}px`);

      // Debug logging for smart arrow positioning
      if (arrowOffset !== 0) {
        isDev() &&
          console.log(
            '[TooltipManager] SMART ARROW: Tooltip shifted, arrow offset applied',
            {
              iconCenterX,
              tooltipLeftPosition,
              arrowOffset: `${arrowOffset}px`,
              tooltipWidth: tooltipRect.width,
              screenWidth: windowWidth,
            }
          );
      }
    } catch (error) {
      isDev() &&
        console.warn('[TooltipManager] Error positioning tooltip:', error);
    }
  };

  /**
   * Hide a tooltip
   * @param {HTMLElement} tooltipWrapper - The tooltip wrapper element
   */
  const hideTooltip = (tooltipWrapper) => {
    if (!tooltipWrapper) return;

    // Pause any videos in the tooltip before hiding
    try {
      const videos = tooltipWrapper.querySelectorAll('video');
      if (videos.length > 0) {
        videos.forEach((video) => {
          try {
            video.pause();
          } catch (e) {}
        });
      }
    } catch (e) {}

    // Remove visibility class
    tooltipWrapper.classList.remove(TOOLTIP_CLASSES.VISIBLE);

    // If tooltip is attached to body (our new method), remove it after animation completes
    if (tooltipWrapper.parentElement === document.body) {
      setTimeout(() => {
        if (tooltipWrapper.parentElement) {
          tooltipWrapper.parentElement.removeChild(tooltipWrapper);
        }
      }, 300); // Match this with CSS transition duration
    }

    // Clear active tooltip reference if this is the active one
    if (activeTooltip === tooltipWrapper) {
      activeTooltip = null;
    }
  };

  /**
   * Check if a category has tooltip content
   * PERFORMANCE CRITICAL: This function is called during HTML generation for every category
   * It MUST be synchronous and cache-only to avoid blocking the render pipeline
   * @param {string} categoryId - Category ID
   * @returns {boolean} True if category has tooltip content
   */
  const hasTooltipContent = (categoryId) => {
    if (!categoryId) return false;

    // CACHE-ONLY CHECK: Never hit conductor during HTML generation
    // Cache is pre-populated by preCacheAllTooltipData() on system:initialized
    const hasCachedTooltip =
      tooltipsData.has(categoryId) &&
      ((tooltipsData.get(categoryId).text &&
        (typeof tooltipsData.get(categoryId).text === 'string'
          ? tooltipsData.get(categoryId).text.trim() !== ''
          : typeof tooltipsData.get(categoryId).text === 'object')) ||
        (tooltipsData.get(categoryId).html &&
          typeof tooltipsData.get(categoryId).html === 'string' &&
          tooltipsData.get(categoryId).html.trim() !== ''));

    if (hasCachedTooltip) {
      // Initialize tooltip state if not already set
      if (!tooltipStates.has(categoryId)) {
        tooltipStates.set(categoryId, false); // Start closed
      }

      return true;
    }

    // PERFORMANCE: If cache is not pre-populated yet, this is expected during early initialization
    // The cache will be populated by preCacheAllTooltipData() before navigation renders categories
    if (!cachePrePopulated) {
      isDev() &&
        console.warn(
          'PERFORMANCE WARNING: hasTooltipContent() called before cache was populated.',
          { categoryId }
        );
      // Return false during pre-cache phase - this is expected and safe
      return false;
    }

    // If we reach here after cache is pre-populated, the category legitimately has no tooltip
    return false;
  };

  /**
   * Handle custom tooltip registration
   * PATTERN 14: Try/Catch + Failed Events
   * @param {Object} eventData - Event data containing categoryId and tooltipData
   */
  const handleCustomTooltipRegistration = (eventData) => {
    try {
      if (!eventData || !eventData.categoryId || !eventData.tooltipData) {
        isDev() &&
          console.warn(
            '[TooltipManager] Invalid custom tooltip registration data:',
            eventData
          );
        return;
      }

      const { categoryId, tooltipData } = eventData;

      // 🚨 THIS OBJECT IS AN ALLOWLIST, AND ANYTHING MISSING FROM IT IS SILENTLY
      // DROPPED. `variantClass` was added to the registration payload and to
      // showTooltip() but not here, so it never survived the round trip: the
      // pass-through tooltip registered with a variant, stored three fields,
      // and rendered unstyled. Measured on the preview theme —
      // `hasVariantClass: false` on both triggers while the source on disk and
      // the served asset both contained the field.
      //
      // Build the stored record ONCE so the registered and re-registered paths
      // cannot diverge; only the state seeding differs between them.
      const storedTooltipData = {
        text: tooltipData.text || '',
        html: tooltipData.html || '',
        desktopOnly: tooltipData.desktopOnly || false,
        variantClass: tooltipData.variantClass || '',
      };

      // Update data if already registered (e.g. forbidden tooltips change on
      // re-evaluation, and the pass-through tooltip re-registers whenever the
      // customer's measurements change); otherwise register fresh.
      const alreadyRegistered = tooltipsData.has(categoryId);
      tooltipsData.set(categoryId, storedTooltipData);
      if (!alreadyRegistered) {
        // Initialize tooltip state
        tooltipStates.set(categoryId, false);
      }
      // Note: Listener re-attachment is triggered by the UI_INITIALIZE_TOOLTIPS
      // event, published after all custom tooltips for an update are registered.

      isDev() &&
        console.log(
          `[TooltipManager] Custom tooltip registered for category: ${categoryId}`
        );

      // Attach listeners via the coalesced pass rather than scanning the whole
      // document for THIS category's trigger. See scheduleTriggerAttach() for
      // the measurement that motivated this: the callers publish once per
      // forbidden variant, so the old per-registration document scan ran 926
      // times per selection and cost ~240ms of main-thread blocking.
      scheduleTriggerAttach();
    } catch (error) {
      isDev() &&
        console.error(
          '[TooltipManager] Error handling custom tooltip registration:',
          error
        );
      // Route through centralized error handler (DRY: single publication point)
      window.MurphyDoorConfigurator.utils.handleSystemError({
        type: 'TooltipError',
        error: error,
        source: 'tooltipManager.handleCustomTooltipRegistration',
        recoverable: true,
        impact: 'low',
      });
    }
  };

  /**
   * Handle new category creation to set up tooltip listeners
   * @param {Object} eventData - Event data containing categoryId
   */
  const handleCategoryCreated = (eventData) => {
    try {
      if (!eventData || !eventData.categoryId) return;

      // Coalesced for the same reason as the registration path: categories are
      // created in bursts during a re-render, and each per-category lookup was a
      // document-wide scan of a four-part selector list.
      scheduleTriggerAttach();
    } catch (error) {
      isDev() &&
        console.warn(
          '[TooltipManager] Error handling category created event:',
          error
        );
    }
  };

  /**
   * Set up tooltip event listeners for a specific icon
   * @param {HTMLElement} icon - Tooltip icon element
   */
  const setupTooltipEventListenersForIcon = (icon) => {
    const categoryId = icon.getAttribute('data-category');

    if (!categoryId) return;

    // ── Idempotence guard ────────────────────────────────────────────────────
    // Every handler below is a stable module-level reference, so re-running this
    // function on an element it has already wired changes NOTHING: per the DOM
    // spec, addEventListener with an identical (type, listener, capture) triple
    // is discarded. The remove/add dance was therefore pure cost — measured at
    // ~4,930 listener operations per selection across the configurator.
    //
    // The signature captures every input that decides WHICH listeners get bound,
    // so a genuine change (a category whose desktopOnly flag flipped, or a
    // freshly rendered element, which is a different object and misses the
    // WeakMap) still re-wires. Keyed by element identity, so an innerHTML
    // re-render that replaces the node is correctly treated as new.
    const tooltipEntryForSignature = tooltipsData.get(categoryId);
    const signature =
      categoryId +
      '|' +
      (isMobileDevice ? 'm' : 'd') +
      '|' +
      (tooltipEntryForSignature && tooltipEntryForSignature.desktopOnly
        ? '1'
        : '0');

    if (wiredTriggers.get(icon) === signature) return;

    // Remove existing listeners to prevent duplicates
    icon.removeEventListener('mouseover', handleTooltipHover);
    icon.removeEventListener('mouseout', handleTooltipMouseOut);
    icon.removeEventListener('click', handleTooltipClick);
    icon.removeEventListener('keydown', handleTooltipKeydown);

    // Ensure the icon is properly styled for interaction
    icon.style.pointerEvents = 'auto';

    // Make sure any SVG inside the icon doesn't block mouse events
    // For both regular icons AND buttons, SVGs should not block pointer events
    // This ensures hover/click events properly bubble up to the parent element
    const svgElement = icon.querySelector('svg');
    if (svgElement) {
      svgElement.style.pointerEvents = 'none';
    }

    // Add event listeners based on device type
    const tooltipEntry = tooltipsData.get(categoryId);
    const isDesktopOnly = tooltipEntry && tooltipEntry.desktopOnly;

    if (!isMobileDevice) {
      // Desktop hover events
      icon.addEventListener('mouseover', handleTooltipHover);
      icon.addEventListener('mouseout', handleTooltipMouseOut);
    }

    // Click events: skip on mobile for desktop-only tooltips
    const shouldAddClickListener = !isMobileDevice || !isDesktopOnly;
    if (shouldAddClickListener) {
      icon.addEventListener('click', handleTooltipClick);
    }

    // Keyboard activation is bound UNCONDITIONALLY, unlike click.
    // The desktop-only skip above exists because a hover tooltip has no useful tap
    // behaviour on a touch device — but a keyboard user on a small viewport still
    // needs to reach it, and leaving role="button" tabindex="0" on an element with
    // no key handler is the WCAG 2.1.1 failure this fixes.
    icon.addEventListener('keydown', handleTooltipKeydown);

    // Recorded LAST so an exception anywhere above leaves the element unmarked
    // and the next pass retries it, rather than caching a half-wired trigger.
    wiredTriggers.set(icon, signature);
  };

  // Check if a tooltip is currently open via click
  const isTooltipOpen = (categoryId) => {
    return tooltipStates.get(categoryId) || false;
  };

  /**
   * Stable DOM id for a category's tooltip popup.
   * Non-alphanumerics are replaced because categoryIds contain spaces and
   * ampersands ("Wood & Finish"), which are legal in an id but awkward in the
   * attribute selectors this file already builds by hand.
   */
  const tooltipDomId = (categoryId) =>
    'md-tooltip-' + String(categoryId).replace(/[^a-zA-Z0-9_-]+/g, '-');

  /**
   * Mirror the open/closed state onto the TRIGGER for assistive tech.
   *
   * Lives inside setTooltipState so it runs on EVERY path that opens or closes a
   * tooltip -- pointer, keyboard, Escape, outside-click and the event-bus handler
   * all funnel through here. Wiring it only into the keyboard handler would leave
   * a trigger claiming aria-expanded="true" after an outside click closed it.
   */
  const syncTriggerAria = (categoryId, isOpen) => {
    try {
      document
        .querySelectorAll(
          triggerSelectorFor(categoryId)
        )
        .forEach((trigger) => {
          // Only annotate real controls. Several .murphy-door-info-icon elements
          // are decorative divs with no role and no tabindex; aria-expanded on a
          // non-interactive node is noise.
          if (trigger.getAttribute('role') !== 'button') return;
          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          if (isOpen) {
            trigger.setAttribute('aria-describedby', tooltipDomId(categoryId));
          } else {
            trigger.removeAttribute('aria-describedby');
          }
        });
    } catch (error) {
      isDev() && console.warn('[TooltipManager] trigger aria sync failed:', error);
    }
  };

  // Set tooltip state (open or closed)
  const setTooltipState = (categoryId, isOpen) => {
    if (categoryId && tooltipsData.has(categoryId)) {
      tooltipStates.set(categoryId, isOpen);
      syncTriggerAria(categoryId, isOpen);

      // PATTERN 10: UI Update Events
      publishEvent(EVENTS.UI_TOOLTIP_STATE_CHANGED, {
        categoryId,
        isOpen,
        timestamp: Date.now(),
      });

      return true;
    }
    return false;
  };

  /**
   * PATTERN 15: Cleanup all event subscriptions and reset module state.
   */
  const cleanup = () => {
    isDev() &&
      console.log(
        `[TooltipManager] Cleaning up ${cleanupHandlers.length} subscriptions`
      );
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (error) {
        isDev() &&
          console.error('[TooltipManager] Cleanup handler error:', error);
      }
    });
    cleanupHandlers.length = 0;

    // Clear any pending hide timeouts to prevent dangling setTimeout calls
    hideTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    hideTimeouts.clear();

    bootstrapComplete = false;
    pendingCalls = [];
    initialized = false;
    cachePrePopulated = false;
    tooltipsData.clear();
    tooltipStates.clear();
    activeTooltip = null;

    publishEvent(EVENTS.UI_TOOLTIP_MANAGER_CLEANUP, {
      module: 'TooltipManager',
      timestamp: Date.now(),
    });
  };

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // ============================================================================
  window.MurphyDoorConfigurator.TooltipManager = {
    initialize,
    hasTooltipContent,
    isTooltipOpen,
    setTooltipState,
    extractTextFromNestedTooltip,
    setConductor,
    getConductor,
    cleanup,

    /**
     * Get current module bootstrap state for debugging.
     * PATTERN 18: Returns frozen state object.
     * @returns {Object} Current module state (frozen)
     */
    getBootstrapState() {
      return Object.freeze({
        bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        cleanupHandlerCount: cleanupHandlers.length,
        initialized,
        cachePrePopulated,
        tooltipCount: tooltipsData.size,
      });
    },
  };

  // Initialize when DOM is ready (SINGLE initialization - no duplicates!)
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    initialize();

    // Attach to conductor if available using CCP-compliant access
    const conductorRef = getConductor();
    if (conductorRef && conductorRef.registerModule) {
      isDev() &&
        console.log(
          '[TooltipManager] CCP COMPLIANCE: registering via conductor.registerModule()'
        );
      conductorRef.registerModule(
        'TooltipManager',
        window.MurphyDoorConfigurator.TooltipManager
      );
    }
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();

      // Attach to conductor if available using CCP-compliant access
      const conductorRef = getConductor();
      if (conductorRef && conductorRef.registerModule) {
        isDev() &&
          console.log(
            '[TooltipManager] CCP COMPLIANCE: registering via conductor.registerModule()'
          );
        conductorRef.registerModule(
          'TooltipManager',
          window.MurphyDoorConfigurator.TooltipManager
        );
      }
    });
  }
})(window);
